import { cva, type VariantProps } from "class-variance-authority";
import clsx from "clsx";
import { useRef } from "react";
import { Separator } from "react-resizable-panels";
import { Icon } from "../icons";
import type { ChildrenProps, ClassNameProps } from "../types";
import { PanelContext, usePanelsContext } from "./context";

/** Props Interface */
interface PanelProps extends ChildrenProps, ClassNameProps, VariantProps<typeof panels> {
	flex?: 0 | 1 | 1.5 | 2 | 2.5 | 3 | 4 | 5 | 6 | 7 | 8;
}

export function Panel({ children, flex = 1, className }: PanelProps) {
	/** Context */
	const { resizable } = usePanelsContext();

	/** Hooks */
	const ref = useRef<HTMLDivElement>(null);

	/** Render */
	return (
		<PanelContext.Provider value={{ ref }}>
			<div
				ref={ref}
				className={clsx(
					"scrollbar-hidden top-0 md:h-full min-h-0 overflow-y-auto px-[0.5px]",
					"flex flex-col gap-4",
					className,
				)}
				style={{
					flex,
				}}
			>
				{children}
			</div>
			{resizable && (
				<Separator className="-mx-4 w-4 flex hover:*:flex flex-col items-center gap-2">
					<div className="w-0 border-r border-dashed border-primary-300 hidden flex-1" />
					<Icon
						name="ChevronsRightLeft"
						className="hidden stroke-primary-300"
						size={14}
					/>
					<div className="w-0 border-r border-dashed border-primary-300 hidden flex-1" />
				</Separator>
			)}
		</PanelContext.Provider>
	);
}

const panels = cva([], {
	variants: {
		flex: {
			0: "",
			1: "md:flex-1",
			1.5: "md:flex-[1.5]",
			2: "md:flex-2",
			2.5: "md:flex-[2.5]",
			3: "md:flex-3",
			4: "md:flex-4",
			5: "md:flex-5",
			6: "md:flex-6",
			7: "md:flex-7",
			8: "md:flex-8",
		},
	},
	defaultVariants: {
		flex: 1,
	},
});
